import { useEffect, useRef, useState } from 'react';

import dayjs from 'dayjs';

import { CELL_WIDTH_REM } from '@/modules/planner/constants';
import { useAutoScroll } from '@/modules/planner/hooks/scroll/use-auto-scroll';
import usePlannerStore from '@/modules/planner/store';

interface UseAssignmentDragResizeProps {
  assignmentId: number;
  startDate: dayjs.Dayjs;
  endDate: dayjs.Dayjs;
  originalStartDate: dayjs.Dayjs;
  originalEndDate: dayjs.Dayjs;
  disabled?: boolean;
  onUpdate?: (newStart: dayjs.Dayjs, newEnd: dayjs.Dayjs) => void;
}

export const useAssignmentDragResize = ({
  assignmentId,
  startDate,
  endDate,
  originalStartDate,
  originalEndDate,
  disabled,
  onUpdate,
}: UseAssignmentDragResizeProps) => {
  const dates = usePlannerStore(store => store.dates);
  const zoomLevel = usePlannerStore(store => store.zoomLevel);
  const setActiveDragResizeId = usePlannerStore(store => store.setActiveDragResizeId);
  const setInteractingAssignmentId = usePlannerStore(store => store.setInteractingAssignmentId);
  const setIsDragResizeInteracting = usePlannerStore(store => store.setIsDragResizeInteracting);

  const [isResizing, setIsResizing] = useState<null | 'left' | 'right'>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showPhantom, setShowPhantom] = useState(false);
  const [tempStart, setTempStart] = useState(startDate);
  const [tempEnd, setTempEnd] = useState(endDate);
  const [isOptimisticMode, setIsOptimisticMode] = useState(false);

  const startX = useRef<number | null>(null);
  const phantomRef = useRef<HTMLDivElement>(null);
  const didMove = useRef<boolean>(false);
  const shouldBlockClick = useRef<boolean>(false);
  const optimisticStart = useRef<dayjs.Dayjs | null>(null);
  const optimisticEnd = useRef<dayjs.Dayjs | null>(null);
  const autoScrollStarted = useRef<boolean>(false);
  const { lastMouseXRef, startAutoScroll, stopAutoScroll } = useAutoScroll();

  const cellSize = CELL_WIDTH_REM * zoomLevel;
  const baseDate = dates[0];

  // Display logic:
  // 1. During drag/resize (after movement threshold): show full assignment (phantom)
  // 2. After release (optimistic mode): show optimistically updated full assignment
  // 3. After server response: show new fragment dates from props
  const displayStart = (() => {
    if (showPhantom && (isDragging || isResizing)) {
      return originalStartDate; // Show full assignment during interaction
    }
    if (isOptimisticMode && optimisticStart.current) {
      return optimisticStart.current; // Show optimistic full assignment position
    }
    return startDate; // Use fragment dates from props
  })();

  const displayEnd = (() => {
    if (showPhantom && (isDragging || isResizing)) {
      return originalEndDate;
    }
    if (isOptimisticMode && optimisticEnd.current) {
      return optimisticEnd.current;
    }
    return endDate;
  })();

  const startOffset = displayStart.startOf('day').diff(baseDate.startOf('day'), 'day') * cellSize;
  const duration =
    (displayEnd.startOf('day').diff(displayStart.startOf('day'), 'day') + 1) * cellSize;

  // Reset temp positions to original dates (used when validation fails)
  const resetPosition = () => {
    setTempStart(startDate);
    setTempEnd(endDate);
    setIsOptimisticMode(false);
    optimisticStart.current = null;
    optimisticEnd.current = null;
  };

  // Set interacting flag immediately to block tooltips
  useEffect(() => {
    setIsDragResizeInteracting(isDragging || isResizing !== null);
  }, [isDragging, isResizing, setIsDragResizeInteracting]);

  // Release the global drag/resize lock as soon as the pointer interaction ends.
  // The optimistic fragment ownership is cleared separately after the optimistic
  // render is reconciled with the updated assignment data.
  useEffect(() => {
    if (!isDragging && !isResizing) {
      setActiveDragResizeId(null);
    }
  }, [isDragging, isResizing, setActiveDragResizeId]);

  // Keep sibling fragments hidden while the released fragment renders its
  // optimistic full-assignment state. Clear once the optimistic phase ends.
  useEffect(() => {
    if (!isDragging && !isResizing && !isOptimisticMode) {
      setInteractingAssignmentId(null);
    }
  }, [isDragging, isResizing, isOptimisticMode, setInteractingAssignmentId]);

  // If the optimistic fragment unmounts before it can reconcile with server data,
  // release any global interaction ownership it still holds. Otherwise all fresh
  // fragments for this assignment can stay hidden because the store still thinks
  // this assignment is "interacting".
  useEffect(() => {
    return () => {
      const store = usePlannerStore.getState();

      if (store.interactingAssignmentId === assignmentId) {
        store.setInteractingAssignmentId(null);
      }

      if (store.activeDragResizeId === assignmentId) {
        store.setActiveDragResizeId(null);
      }

      if (store.isDragResizeInteracting) {
        store.setIsDragResizeInteracting(false);
      }
    };
  }, [assignmentId]);

  // Detect when server data arrives after optimistic update
  // Server responds -> parent refetches with updated assignment -> original dates match optimistic values
  useEffect(() => {
    if (isOptimisticMode && !isDragging && !isResizing) {
      // Check if server confirmed the update by comparing original assignment dates with optimistic values
      const serverDataArrived =
        optimisticStart.current?.isSame(originalStartDate, 'day') &&
        optimisticEnd.current?.isSame(originalEndDate, 'day');

      if (serverDataArrived) {
        // Server confirmed the update, exit optimistic mode and use new fragment dates
        setIsOptimisticMode(false);
        optimisticStart.current = null;
        optimisticEnd.current = null;
      }
    }
  }, [originalStartDate, originalEndDate, isOptimisticMode, isDragging, isResizing]);

  // Sync temp state with props when they change (e.g., after cut/update operations)
  useEffect(() => {
    // Only update if not currently dragging/resizing and dates have actually changed
    if (!isDragging && !isResizing && !isOptimisticMode) {
      if (!tempStart.isSame(startDate, 'day')) {
        setTempStart(startDate);
      }
      if (!tempEnd.isSame(endDate, 'day')) {
        setTempEnd(endDate);
      }
    }
  }, [startDate, endDate, isDragging, isResizing, isOptimisticMode, tempStart, tempEnd]);

  const handleMouseDown = (
    direction: 'left' | 'right',
    event: React.MouseEvent<HTMLDivElement>
  ) => {
    if (disabled) return;
    setIsResizing(direction);
    startX.current = event.clientX;
    lastMouseXRef.current = event.clientX;
    didMove.current = false;
    shouldBlockClick.current = false;
  };

  const handleDragStart = (event: React.MouseEvent<HTMLDivElement>) => {
    if (disabled) return;
    // TODO: update with custom class instead of cursor decorator
    if ((event.target as HTMLElement).classList.contains('cursor-ew-resize')) return;
    setIsDragging(true);
    startX.current = event.clientX;
    lastMouseXRef.current = event.clientX;
    didMove.current = false;
    shouldBlockClick.current = false;
  };

  useEffect(() => {
    /** Update phantom element position based on current startX offset */
    const updatePhantom = () => {
      if (!phantomRef.current || startX.current === null) return;
      const changePixels = lastMouseXRef.current - startX.current;
      const remSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
      const changeRem = changePixels / remSize;

      if (isDragging) {
        phantomRef.current.style.transform = `translateX(${changeRem}rem)`;
      } else if (isResizing === 'left') {
        phantomRef.current.style.left = `${changeRem}rem`;
        phantomRef.current.style.width = `calc(100% - ${changeRem}rem)`;
      } else {
        phantomRef.current.style.width = `calc(100% + ${changeRem}rem)`;
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      if ((!isResizing && !isDragging) || startX.current === null) return;

      lastMouseXRef.current = e.clientX;

      const changePixels = e.clientX - startX.current;

      // Mark that actual movement occurred (threshold: 2 pixels)
      if (Math.abs(changePixels) > 2 && !didMove.current) {
        didMove.current = true;
        shouldBlockClick.current = true;
        // Set IDs immediately when threshold is crossed to hide fragments
        setInteractingAssignmentId(assignmentId);
        setActiveDragResizeId(assignmentId);
        // Show phantom only after movement threshold is crossed
        setShowPhantom(true);
      }

      // Start auto-scroll only after user begins moving — prevents instant scroll when
      // mousedown happens near an edge
      if (didMove.current && !autoScrollStarted.current) {
        autoScrollStarted.current = true;
        startAutoScroll(scrollAmount => {
          if (startX.current !== null) {
            startX.current -= scrollAmount;
          }
          updatePhantom();
        });
      }

      updatePhantom();
    };

    const handleMouseUp = (e: MouseEvent) => {
      stopAutoScroll();

      // If no actual movement occurred, cancel the drag/resize immediately
      // This prevents ghost drag state when user clicks to open modal
      if (!didMove.current) {
        setIsResizing(null);
        setIsDragging(false);
        setShowPhantom(false);
        startX.current = null;
        didMove.current = false;
        shouldBlockClick.current = false;
        return;
      }

      if ((!isResizing && !isDragging) || !onUpdate) {
        setIsResizing(null);
        setIsDragging(false);
        setShowPhantom(false);
        startX.current = null;
        return;
      }

      if (!phantomRef.current) return;

      const remSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
      const phantomStyles = phantomRef.current.style;

      let daysShift;

      if (isDragging) {
        const transform = phantomStyles.transform;
        const translateX = transform
          ? parseFloat(transform.match(/translateX\(([-\d.]+)rem\)/)?.[1] || '0')
          : 0;
        daysShift = Math.round((translateX * remSize) / (CELL_WIDTH_REM * remSize * zoomLevel));
      } else {
        const phantomLeft = phantomStyles.left ? parseFloat(phantomStyles.left) || 0 : 0;
        const widthRemShift = parseFloat(
          phantomStyles.width
            ?.match(/calc\(100% ([+-] \d+(?:\.\d+)?rem)\)/)?.[1]
            ?.replace('rem', '')
            .replace(' ', '') || '0'
        );
        daysShift = Math.round(
          ((isResizing === 'left' ? phantomLeft : widthRemShift) * remSize) /
            (CELL_WIDTH_REM * remSize * zoomLevel)
        );
      }

      // Always use original assignment dates for both drag and resize
      // Fragment-level protection is handled by isResizeLeftDisabled/isResizeRightDisabled
      let newStart = originalStartDate;
      let newEnd = originalEndDate;

      if (isDragging) {
        // Move entire assignment
        newStart = originalStartDate.add(daysShift, 'day');
        newEnd = originalEndDate.add(daysShift, 'day');
      } else if (isResizing === 'left') {
        // Resize from left edge
        newStart = originalStartDate.add(daysShift, 'day');
        // Prevent start from going past end
        if (newStart.isAfter(originalEndDate, 'day')) {
          newStart = originalEndDate;
        }
      } else if (isResizing === 'right') {
        // Resize from right edge
        newEnd = originalEndDate.add(daysShift, 'day');
        // Prevent end from going before start
        if (newEnd.isBefore(originalStartDate, 'day')) {
          newEnd = originalStartDate;
        }
      }

      // Check if dates have actually changed
      const hasStartChanged = !newStart.isSame(originalStartDate, 'day');
      const hasEndChanged = !newEnd.isSame(originalEndDate, 'day');

      if (hasStartChanged || hasEndChanged) {
        // Enter optimistic mode: show full assignment at new position
        // until server responds and fragments are recalculated
        setIsOptimisticMode(true);
        optimisticStart.current = newStart;
        optimisticEnd.current = newEnd;

        onUpdate(newStart, newEnd);
      }

      setIsResizing(null);
      setIsDragging(false);
      setShowPhantom(false);
      startX.current = null;

      // Reset flags after a delay to allow click handler to check them first
      setTimeout(() => {
        didMove.current = false;
        shouldBlockClick.current = false;
      }, 100);
    };

    // Cancel drag/resize on Escape key press
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && (isDragging || isResizing)) {
        stopAutoScroll();
        autoScrollStarted.current = false;
        setIsResizing(null);
        setIsDragging(false);
        setShowPhantom(false);
        startX.current = null;
        didMove.current = false;
        shouldBlockClick.current = false;

        // Reset phantom position
        if (phantomRef.current) {
          phantomRef.current.style.transform = '';
          phantomRef.current.style.left = '';
          phantomRef.current.style.width = '';
        }
      }
    };

    if (isResizing || isDragging) {
      document.addEventListener('mousemove', handleMouseMove, { capture: true, passive: false });
      document.addEventListener('mouseup', handleMouseUp, { capture: true, passive: false });
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      stopAutoScroll();
      autoScrollStarted.current = false;
      document.removeEventListener('mousemove', handleMouseMove, { capture: true });
      document.removeEventListener('mouseup', handleMouseUp, { capture: true });
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    isResizing,
    isDragging,
    tempStart,
    tempEnd,
    onUpdate,
    zoomLevel,
    startDate,
    endDate,
    originalStartDate,
    originalEndDate,
    startAutoScroll,
    stopAutoScroll,
  ]);

  return {
    phantomRef,
    isDragging,
    isResizing,
    isOptimisticMode,
    showPhantom,
    tempStart,
    tempEnd,
    startOffset,
    duration,
    handleDragStart,
    handleMouseDown,
    resetPosition,
    didMove,
    shouldBlockClick,
  };
};
